import { uid } from '@runtime/shared/utils/id'
import { computeLevelFromRating } from '@runtime/core/user/userModel'
import type {
  AppStoreState,
  GameSummary,
} from '@runtime/core/store/appStoreTypes'
import type { AppStoreSlice } from './sliceTypes'
import {
  applyGameResult,
  pendingResultFromHistory,
} from '@runtime/core/user/gameResults'
import {
  pickInitialGameResultsOutbox,
  pickInitialGameResultReceipts,
} from '@runtime/core/store/appStorePersist'
import { historyFromGameSummary } from '@runtime/core/user/gameSummaryHistory'
import {
  FullGameStorageError,
  fullGameOwnerKey,
  saveOwnedFullGame,
} from '@runtime/core/user/ownedFullGames'

type GamesSlice = Pick<
  AppStoreState,
  | 'recordGame'
  | 'recordGameForOwner'
  | 'toggleFavoriteGame'
  | 'gameResultsVersion'
  | 'gameResultsOutbox'
  | 'gameResultReceipts'
  | 'gameResultsStorageIssue'
  | 'acknowledgeGameResults'
>
export const createGamesSlice: AppStoreSlice<GamesSlice> = (set, get) => ({
  gameResultsOutbox: pickInitialGameResultsOutbox(),
  gameResultReceipts: pickInitialGameResultReceipts(),
  gameResultsStorageIssue: null,
  gameResultsVersion: 1,
  acknowledgeGameResults: (userId, ids, snapshot) =>
    set((state) => {
      const pending = state.gameResultsOutbox[userId] ?? []
      const acknowledged = new Set(ids)
      const acceptedAt = Date.now()
      const received = Object.fromEntries(
        pending
          .filter((result) => acknowledged.has(result.id))
          .map((result) => [result.id, { acceptedAt }]),
      )
      const gameResultReceipts = {
        ...state.gameResultReceipts,
        [userId]: { ...state.gameResultReceipts[userId], ...received },
      }
      const remaining = pending.filter((result) => !acknowledged.has(result.id))
      const gameResultsOutbox = {
        ...state.gameResultsOutbox,
        [userId]: remaining,
      }
      if (state.user?.id !== userId)
        return { gameResultsOutbox, gameResultReceipts }
      const projected = remaining.reduce(applyGameResult, snapshot)
      return {
        gameResultsOutbox,
        gameResultReceipts,
        user: {
          ...state.user,
          ...projected,
          level: computeLevelFromRating(projected.rating),
        },
      }
    }),
  recordGameForOwner: (ownerId, input) => {
    if (!input) return
    const summary = {
      ...input,
      id: input.id ?? uid(),
      timestamp: input.timestamp ?? Date.now(),
    }
    const entry = historyFromGameSummary(summary)
    if (!entry) return
    const state = get()
    if (
      ownerId &&
      (state.gameResultReceipts[ownerId]?.[entry.id] ||
        state.gameResultsOutbox[ownerId]?.some(
          (result) => result.id === entry.id,
        ) ||
        (state.user?.id === ownerId &&
          state.user.history?.some((row) => row.id === entry.id)))
    )
      return
    try {
      entry.fullGameRef = saveOwnedFullGame(fullGameOwnerKey(ownerId), {
        ...summary,
        id: entry.id,
        timestamp: entry.ts,
      })
      set({ gameResultsStorageIssue: null })
    } catch (error) {
      set({
        gameResultsStorageIssue:
          error instanceof FullGameStorageError ? error.kind : 'write',
      })
    }
    if (!ownerId) return // Device history is never adopted or sent by a later login.
    const pending = pendingResultFromHistory(entry)
    if (!pending) return
    set((current) => {
      if (
        current.gameResultReceipts[ownerId]?.[entry.id] ||
        current.gameResultsOutbox[ownerId]?.some(
          (result) => result.id === entry.id,
        )
      )
        return {}
      const gameResultsOutbox = {
        ...current.gameResultsOutbox,
        [ownerId]: [...(current.gameResultsOutbox[ownerId] ?? []), pending],
      }
      if (current.user?.id !== ownerId) return { gameResultsOutbox }
      const previous = current.user
      const projected = applyGameResult(
        { games: previous.games ?? {}, rating: previous.rating ?? 1200 },
        pending,
      )
      return {
        gameResultsOutbox,
        user: {
          ...previous,
          ...projected,
          level: computeLevelFromRating(projected.rating),
          history: [entry, ...(previous.history ?? [])].slice(0, 50),
          favorites: previous.favorites ?? [],
        },
      }
    })
  },
  recordGame: (summary) => {
    const state = get(),
      user = state.user
    if (!summary || !user) return
    let meIndex = summary.meIndex
    if (meIndex == null && user.pseudo)
      meIndex = summary.players?.findIndex(
        (player) => player.name?.toLowerCase() === user.pseudo?.toLowerCase(),
      )
    state.recordGameForOwner(user.id, { ...summary, meIndex })
  },
  toggleFavoriteGame: (gameId) => {
    if (!gameId) return
    set((state) => {
      if (!state.user) return {}
      const favorites = [...(state.user.favorites ?? [])],
        index = favorites.indexOf(gameId)
      if (index === -1) favorites.push(gameId)
      else favorites.splice(index, 1)
      return { user: { ...state.user, favorites } }
    })
  },
})
